/**
 * Stable lock/preview key for a floor-plan measurement.
 *
 * Single source of truth, shared by the stretch module (`wallStretch.js`), the
 * stretch hook (`useStretchPlan.js`), and the canvas draw layer
 * (`measurementUtilsV2.js`) — previously this logic was duplicated byte-for-byte
 * in wallStretch + the draw layer. Kept dependency-free so the draw layer
 * doesn't pull in the stretch module.
 *
 * Opening measurements (width / position) carry a pre-computed `lockKey` stamped
 * at generation time (the key needs the structure-opening id, which the raw
 * measurement doesn't carry). When present it wins, so openings and walls share
 * one `measurementLocks` store, one locked-keys set, and one 🔒 glyph path.
 */
export const getMeasurementKey = (measurement) => {
  if (!measurement) return null;
  // Pre-stamped opening key (opening-width:{id} / opening-pos:{id}).
  if (measurement.lockKey) return measurement.lockKey;
  if (measurement.subtype === 'wall-length' && measurement.wallId) {
    return `wall:${measurement.wallId}`;
  }
  if (measurement.subtype === 'node-based') {
    const wallId = measurement.wallId || null;
    if (!wallId) return null; // v2 prerequisite: needs wallId on the measurement
    const idA =
      measurement.fromObjectId || `wallEnd:${measurement.fromLabel || 'A'}`;
    const idB =
      measurement.toObjectId || `wallEnd:${measurement.toLabel || 'B'}`;
    const [first, second] = [idA, idB].sort();
    return `partial:${wallId}:${first}:${second}`;
  }
  return null;
};
